import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { getMessaging, getToken, isSupported, Messaging, onMessage } from 'firebase/messaging';
import { BehaviorSubject, combineLatest, distinctUntilChanged, Observable } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { environment } from 'environment/environment';

/**
 * Push Firebase : une fois connecté, le navigateur demande l'autorisation d'afficher des notifications, puis
 * déclare son jeton au service de notifications, qui pousse alors chaque nouvelle notification du compte sur
 * cet appareil. À la déconnexion, l'appareil est retiré pour que le compte suivant ne reçoive pas celles du précédent.
 */
@Injectable({
    providedIn: 'root',
})
export class FcmService {
    private readonly VAPID_KEY = 'BMi1MrM-aZzSt8QSt9mNX7nEsHml4bOHXFQf5-S5ZkTWQeyQuHwlbwrojvuOd1lZNxvCWg8B1gsvufZh-l6WIh4';
    private readonly devicesUrl = `${environment.API_notifications}/v1/notifications/devices`;

    private _http = inject(HttpClient);
    private _authService = inject(AuthService);
    private messaging: Messaging | null = null;
    private fcmToken$ = new BehaviorSubject<string | null>(null);
    /** Jeton déclaré au serveur pour la session en cours. */
    private registeredToken: string | null = null;
    private _pushActive$ = new BehaviorSubject<boolean>(false);

    constructor(
        private notificationsService: NotificationsService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        if (isPlatformBrowser(this.platformId)) {
            void this.initializeFcm();
        }
    }

    get fcmToken(): Observable<string | null> {
        return this.fcmToken$.asObservable();
    }

    /** Vrai quand cet appareil reçoit les nouvelles notifications en push : inutile alors d'interroger le serveur. */
    get pushActive$(): Observable<boolean> {
        return this._pushActive$.asObservable();
    }

    private async initializeFcm(): Promise<void> {
        try {
            if (!window.isSecureContext) {
                console.info('FCM skipped: insecure context (HTTP).');
                return;
            }

            const supported = await isSupported().catch(() => false);
            if (!supported) {
                console.info('FCM skipped: unsupported browser.');
                return;
            }

            this.messaging = getMessaging();
            this.listenForMessages();

            // La permission n'est demandée qu'à un utilisateur connecté, pas sur la page de connexion.
            this._authService.isLoggedIn$.pipe(distinctUntilChanged()).subscribe((loggedIn) => {
                if (loggedIn) {
                    this.requestPermission();
                } else {
                    this.registeredToken = null;
                    this._pushActive$.next(false);
                }
            });
            combineLatest([this.fcmToken$, this._authService.isLoggedIn$])
                .subscribe(([token, loggedIn]) => {
                    if (token && loggedIn && token !== this.registeredToken) {
                        this.registerDevice(token);
                    }
                });
            this._authService.signingOut$.subscribe((accessToken) => this.unregisterDevice(accessToken));
        } catch (error) {
            console.warn('FCM initialization failed:', error);
        }
    }

    private requestPermission(): void {
        if (!('Notification' in window)) {
            return;
        }

        if (Notification.permission === 'granted') {
            this.getToken();
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                    this.getToken();
                }
            });
        }
    }

    private getToken(): void {
        navigator.serviceWorker.ready.then((registration) => {
            getToken(this.messaging, {
                vapidKey: this.VAPID_KEY,
                serviceWorkerRegistration: registration,
            })
                .then((token) => {
                    if (token) {
                        this.fcmToken$.next(token);
                    }
                })
                .catch((error) => {
                    console.error('❌ Failed to get FCM token:', error);
                });
        });
    }

    private registerDevice(token: string): void {
        this.registeredToken = token;
        this._http.put<void>(this.devicesUrl, { token }).subscribe({
            next: () => this._pushActive$.next(true),
            error: () => {
                this.registeredToken = null;
                this._pushActive$.next(false);
            },
        });
    }

    /** Appelé avant la fin de la session : l'access token n'est déjà plus fourni par l'intercepteur ensuite. */
    private unregisterDevice(accessToken: string): void {
        const token = this.registeredToken;
        this.registeredToken = null;
        this._pushActive$.next(false);
        if (!token) {
            return;
        }
        this._http.post<void>(`${this.devicesUrl}/unregister`, { token }, {
            headers: new HttpHeaders({ Authorization: `Bearer ${accessToken}` }),
        }).subscribe({ error: () => {} });
    }

    private listenForMessages(): void {
        onMessage(this.messaging, (payload) => {
            const { notification, data } = payload;

            const appNotification = {
                // Même id que la notification en base : la liste rechargée ensuite ne la duplique pas.
                id: data?.['notificationId'] ?? Date.now(),
                title: notification?.title || 'Nouvelle notification',
                body: notification?.body || '',
                createdAt: new Date(),
                read: false,
                eventType: data?.['eventType'] || 'DEFAULT',
                recipientType: data?.['recipientType'] || 'USER',
                data: data || {},
            };

            this.notificationsService.addFcmNotification(appNotification);

            if (document.hidden) {
                this.showNotification(appNotification.title, {
                    body: appNotification.body,
                    icon: '/assets/images/logo/logo-icon.png',
                    data: data || {},
                });
            }
        });
    }

    private showNotification(title: string, options: NotificationOptions): void {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((registration) => {
                registration.showNotification(title, options);
            });
        }
    }
}
